import {
    BaseAsyncAdapter,
    getIdentity,
    IntegrationRuntime,
    type ITridentResource,
    Metamodel,
    ModelMarker,
    OwnedSet,
    ReferenceAdapter,
    UserProperty
} from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { Activity } from 'app/model/pipeline/activity';
import { convertObjectToString, getString } from 'app/shared/utils/string.util';
import { ActivityDependencyAdapter } from './activity-dependency.adapter';
import { ActivityPolicyAdapter } from './activity-policy.adapter';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { Dataset } from 'app/model/dataset/dataset';
import { ActivityUserPropertiesAdapter } from './activity-user-properties.adapter';
import { ParameterValue } from 'app/model/base/parameter-value';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { type ActivityConnectionContext, IActivityContext, IKnownActivityContext, PipelineActivityInterimContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { type ActivityRegistryService } from 'app/authoring/registries/activity-registry.service';
import type { ModelService } from 'app/model/services/model.service';

export abstract class ActivityAsyncAdapter<
    T extends Activity,
    U,
    D extends IActivityContext<string> = IActivityContext<string>
> extends BaseAsyncAdapter<T, U, D> {
    private static readonly policyAdapter = new ActivityPolicyAdapter();
    private static readonly activityUserPropertiesAdapter = new ActivityUserPropertiesAdapter();

    public static linkAllActivitiesFromResource(
        modelActivities: Model.Activity[],
        activities: OwnedSet<Activity>
    ): void {
        modelActivities.forEach((modelActivity: Model.Activity) => {
            if (modelActivity.dependsOn) {
                const toActivity = activities.find((activity: Activity) => activity.name === modelActivity.name);
                if (toActivity) {
                    modelActivity.dependsOn.forEach((fromLink) => {
                        const fromActivity = activities.find(
                            (activity: Activity) => activity.name === fromLink.activity
                        );

                        if (fromActivity) {
                            fromLink.dependencyConditions.forEach((dependencyCondition) => {
                                fromActivity.linkTo(
                                    toActivity,
                                    ActivityDependencyAdapter.convertDependencyConditionToCriteria(dependencyCondition)
                                );
                            });
                        }
                    });
                }
            }
        });
    }

    public static linkAllActivitiesFromFabricContext(
        activityContexts: PipelineActivityInterimContext[],
        activities: OwnedSet<Activity>
    ): void {
        activityContexts.forEach((activityContext: PipelineActivityInterimContext) => {
            if (activityContext.Dependencies) {
                const toActivity = activities.find((activity: Activity) => activity.name === activityContext.Name);
                if (toActivity) {
                    // 1. remove existing link between fromActivity and toActivity
                    toActivity.fromLinks?.forEach((fromLink) => {
                        fromLink.fromActivity.toLinks.delete(fromLink);
                    });
                    toActivity.fromLinks?.clear();
                    // 2. add link
                    activityContext.Dependencies.forEach((dependencyContext) => {
                        const fromActivity = activities.find(
                            (activity: Activity) => activity.name === dependencyContext.ActivityName
                        );
                        if (fromActivity) {
                            dependencyContext.Conditions.forEach((dependencyCondition) => {
                                const criteria =
                                    ActivityDependencyAdapter.convertDependencyConditionContextToCriteria(
                                        dependencyCondition
                                    );
                                fromActivity.linkTo(toActivity, criteria);
                            });
                        }
                    });
                }
            }
        });
    }

    public override async toFabricCopilotContext(activity: T): Promise<D> {
        try {
            if (activity) {
                const activityRegistryService = await getServiceInstance<ActivityRegistryService>(
                    this.context.contextData,
                    'ActivityRegistryService'
                );
                const activityModelDef = Metamodel.modelOf(activity);
                const activityConfiguration = activityRegistryService.getActivityConfiguration(activityModelDef);
                const result = {
                    Name: activity.name,
                    Type: activityConfiguration.isKnownInPipelineContext ? activityModelDef.name : 'Unknown',
                    OriginalActivityType: activityConfiguration.isKnownInPipelineContext
                        ? undefined
                        : activityModelDef.name,
                    Dependencies: new ActivityDependencyAdapter().toResource(activity).map((item) => ({
                        ActivityName: item.activity,
                        Conditions: item.dependencyConditions.map((item) =>
                            ActivityDependencyAdapter.convertDependencyConditionToFabricContext(item)
                        )
                    })),
                    State: activity.state == 'Inactive' ? 'Deactivated' : 'Activated',
                    Timeout: activity.activityType == 'ExecutionActivity' ? activity.policy?.timeout : undefined,
                    RetryCount: activity.activityType == 'ExecutionActivity' ? activity.policy?.retry : undefined,
                };
                return result as unknown as D;
            }
            return undefined;
        } catch (e) {
            this._logWarning(`Failed to convert activity to Fabric context: ${convertObjectToString(e)}`);
            return undefined;
        }
    }

    public override async fromFabricCopilotContext(context: D, model?: T): Promise<T> {
        if (context?.Name && context.Type && context.Type !== 'Unknown') {
            // support create known activty only
            if (!model) {
                const activityModel = Metamodel.isModel(context.Type) ? Metamodel.modelByName(context.Type) : undefined;
                // Activity name should only contain letters, numbers, dashes (-), underscores (_), or spaces.
                model = activityModel?.create<T>(context.Name.replace(/[^a-zA-Z0-9\-_ ]/g, ''));
            }
            model.state = (context as IKnownActivityContext).State === 'Deactivated' ? 'Inactive' : 'Active';
            if (model.activityType === 'ExecutionActivity') {
                model.policy.timeout = (context as IKnownActivityContext).Timeout ?? model.policy.timeout;
                model.policy.retry = (context as IKnownActivityContext).RetryCount ?? model.policy.retry;
            }
        }
        return model;
    }

    protected async getDatasetCopilotContext(dataset: Dataset): Promise<ActivityConnectionContext> {
        const resolveReference = this.context.contextData.contextType === 'mountedDataFactory' && ModelMarker.isProxy(dataset);
        if (resolveReference) {
            // Monitoring adapts the selected definition without hydrating its named ADF dataset references.
            const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
            const resolved = await modelService.fetchByIdentity(getIdentity(dataset), true, dataset.name);
            if (
                !(resolved instanceof Dataset) || ModelMarker.isProxy(resolved) || !resolved.isOk ||
                resolved.name?.toLowerCase() !== dataset.name?.toLowerCase()
            ) {
                throw new Error('The referenced ADF dataset could not be loaded for Copilot.');
            }
            dataset = resolved;
        }
        const adapter = this.context.asyncAdapterFor<Dataset, Model.Dataset, ActivityConnectionContext>(Metamodel.modelOf(dataset));
        if (!adapter) {
            throw new Error('The referenced dataset has no Copilot adapter.');
        }
        const context = await adapter.toFabricCopilotContext(dataset);
        // Location-only projections can be returned when the linked service is missing.
        if (resolveReference && (!context || !context.Name || !context.Type)) {
            throw new Error('The referenced ADF dataset or linked service could not be projected for Copilot.');
        }
        return context;
    }

    
    protected _shouldGetArtifactDMTSConnectionFromSolutionTemplate(): boolean {
        return this.context.adapterConfig.fromSolutionTemplate && this.context.adapterConfig.artifactDMTSCredentials != null;
    }

    protected _getCommonActivityProperties(activity: Activity, activityType: string): Model.ExecutionActivity {
        const modelActivity: Model.ExecutionActivity = {
            name: getString(activity.name),
            description: activity.description,
            type: activityType,
            state: activity.state,
            onInactiveMarkAs: activity.onInactiveMarkAs
        };

        const activityDependencyAdapter: ActivityDependencyAdapter = new ActivityDependencyAdapter();
        modelActivity.dependsOn = activityDependencyAdapter.toResource(activity);

        if (activity.activityType !== 'ControlActivity') {
            modelActivity.policy = ActivityAsyncAdapter.policyAdapter.toResource(activity.policy);
            if (activityType === 'SetVariable' || activityType === 'WebHook' || activityType === 'ExecutePipeline') {
                if (activityType === 'ExecutePipeline') {
                    modelActivity.policy.secureOutput = undefined;
                }
                modelActivity.policy.timeout = undefined;
                modelActivity.policy.retry = undefined;
                modelActivity.policy.retryIntervalInSeconds = undefined;
            }
        }

        if (!this.context.adapterConfig.convertToFabric) {
            modelActivity.userProperties = ActivityAsyncAdapter.activityUserPropertiesAdapter.toResource(
                activity.userProperties
            );

            if (this.context.adapterConfig.dismissEmptyUserProperties && modelActivity.userProperties?.length === 0) {
                modelActivity.userProperties = undefined;
            }
        }
        return modelActivity;
    }

    protected _updatedCommonProperties(modelActivity: Model.ExecutionActivity, activity: Activity): void {
        activity.description = modelActivity.description;
        activity.state = modelActivity.state;
        activity.onInactiveMarkAs = modelActivity.onInactiveMarkAs;
        if (modelActivity.policy) {
            Activity.own(activity, 'policy', ActivityAsyncAdapter.policyAdapter.toModel(modelActivity.policy));
        } else if (activity.activityType !== 'ControlActivity') {
            Activity.own(activity, 'policy', ActivityAsyncAdapter.policyAdapter.toModelWhenNull());
        }

        if (modelActivity.userProperties && modelActivity.userProperties.length) {
            const userProperties: Set<UserProperty> = ActivityAsyncAdapter.activityUserPropertiesAdapter.toModel(
                modelActivity.userProperties
            );
            userProperties.forEach((element) => {
                activity.userProperties.add(element);
            });
        }
    }

    protected _toLinkedServiceReferenceModel(
        linkedService: LinkedService,
        parameters?: ParameterValue[]
    ): Model.LinkedServiceReference {
        return linkedServiceReferenceToResource(linkedService, this.context, this.ensureLogger(), parameters);
    }

    protected _toDatasetReferenceModel(dataset: Dataset): Model.LinkedServiceReference {
        return ReferenceAdapter.toResource(dataset, this.context) as Model.DatasetReference;
    }

    protected _toIntegrationRuntimeModel(connectVia: IntegrationRuntime): Model.IntegrationRuntimeReference {
        return ReferenceAdapter.toResource(connectVia, this.context) as Model.IntegrationRuntimeReference;
    }

    protected async _toAzureResource(tridentResource: ITridentResource): Promise<U> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }
}
